/* WIT Infinite Scroll — shared behaviour for horizontally-scrolling carousel
   tracks (scroll-snap + overflow-x). Makes swiping/dragging/trackpad scroll
   loop seamlessly forever, without touching whatever prev/next arrow markup
   or click handlers a given section already has.

   How it works: the track's real children are cloned once before and once
   after themselves, so the DOM reads [clone][real][clone]. The track starts
   scrolled to the top of the middle ("real") copy. As the user scrolls, once
   they've drifted into either clone (which looks pixel-identical to the real
   set), we silently jump scrollLeft by exactly one set-width to land back in
   the real copy at the same relative offset — the jump is only ever applied
   once scrolling has genuinely stopped moving (not just paused momentarily,
   since trackpad momentum can drift scrollLeft for a while after the last
   scroll event), so it's never visible mid-gesture.

   Usage: add `data-wit-infinite-track` to a scroll container, or call
   window.WitInfiniteScroll.init(trackEl) directly for tracks whose content
   loads asynchronously (call it once the real cards exist).
*/
(function () {
  'use strict';

  function cloneSet(items) {
    return items.map(function (el) {
      var clone = el.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      // Not inert: a clone can be the card on screen (iOS restores or snaps the
      // scroll before a correction runs), and its links must still work on tap.
      // They just stay out of the tab order, since the real set has them.
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (f) {
        f.setAttribute('tabindex', '-1');
      });
      clone.classList.add('wit-infinite-clone');
      return clone;
    });
  }

  // Some sections (e.g. WIT Carousel) are a horizontal-scroll track only
  // below a breakpoint and a plain CSS grid above it. Cloning unconditionally
  // would triple the grid's visible cards on layouts that aren't actually
  // scrollable, so only loop tracks that currently overflow.
  function isScrollable(track) {
    return track.scrollWidth > track.clientWidth + 1;
  }

  // Jump scrollLeft instantly, immune to two things that would otherwise
  // turn this "invisible" repositioning into a visible, buggy-looking scroll:
  //  1. Plain `track.scrollLeft = x` respects the track's CSS
  //     `scroll-behavior` — on any track styled `scroll-behavior: smooth`
  //     (several WIT carousels are, for the arrow-click paging), that turns
  //     every jump into a slow animated scroll instead of an instant one,
  //     which reads as "the carousel bounces back to the start". Passing an
  //     explicit `behavior: 'auto'` to scrollTo() overrides the CSS and
  //     forces a true instant jump.
  //  2. A mandatory scroll-snap container can silently pull a jump back to
  //     the nearest snap point if the target isn't exactly snap-aligned
  //     (e.g. setWidth is a fractional scrollWidth/3) — suspending snap for
  //     the single frame of the jump avoids that.
  function snapSafeJump(track, value) {
    var prevSnap = track.style.scrollSnapType;
    track.style.scrollSnapType = 'none';
    track.scrollTo({ left: value, behavior: 'auto' });
    void track.offsetWidth; // force layout to commit the position before re-enabling snap
    track.style.scrollSnapType = prevSnap;
  }

  function teardown(track) {
    if (track.dataset.witInfiniteInit !== '1') return;
    var clones = track.querySelectorAll('.wit-infinite-clone');
    for (var i = 0; i < clones.length; i++) clones[i].remove();
    track.scrollTo({ left: 0, behavior: 'auto' });
    delete track.dataset.witInfiniteInit;
  }

  function init(track) {
    if (!track || track.dataset.witInfiniteInit === '1') return;
    if (!isScrollable(track)) return; // nothing to loop if it isn't a scroll container

    var originalItems = Array.prototype.slice.call(track.children);
    if (originalItems.length < 2) return; // nothing meaningful to loop

    track.dataset.witInfiniteInit = '1';

    var before = cloneSet(originalItems);
    var after = cloneSet(originalItems);
    var firstReal = track.firstElementChild;
    before.forEach(function (el) { track.insertBefore(el, firstReal); });
    after.forEach(function (el) { track.appendChild(el); });

    // Measure one set from card to card. scrollWidth / 3 also counts the
    // track's end padding, so it lands a few px short of the real first card
    // and mandatory snap then pulls the track onto a clone.
    var setWidth = firstReal.offsetLeft - before[0].offsetLeft;
    snapSafeJump(track, setWidth);

    function correct() {
      var sl = track.scrollLeft;
      if (sl >= setWidth * 2) {
        snapSafeJump(track, sl - setWidth);
      } else if (sl < setWidth) {
        snapSafeJump(track, sl + setWidth);
      }
    }

    // Wait for scrollLeft to actually stop moving before correcting, rather
    // than guessing a fixed delay after the last 'scroll' event. Trackpad
    // momentum can keep drifting the position for anywhere from tens of
    // milliseconds to well over a second depending on the flick — a fixed
    // timeout inevitably fires early sometimes, and correcting while the
    // browser is still actively carrying momentum makes the two fight,
    // which is what "occasionally bounces back" was: a race, not a one-off
    // miscalculation. Polling per-frame until truly stationary has no such
    // race, however long any given flick's momentum lasts.
    var settleRaf = null;
    var lastScrollLeft = null;
    var stableFrames = 0;
    var STABLE_FRAMES_NEEDED = 6; // ~6 frames of zero movement = genuinely stopped

    function watchForSettle() {
      var sl = track.scrollLeft;
      if (sl === lastScrollLeft) {
        stableFrames++;
      } else {
        stableFrames = 0;
        lastScrollLeft = sl;
      }
      if (stableFrames >= STABLE_FRAMES_NEEDED) {
        settleRaf = null;
        correct();
        return;
      }
      settleRaf = window.requestAnimationFrame(watchForSettle);
    }

    track.addEventListener(
      'scroll',
      function () {
        if (settleRaf == null) {
          stableFrames = 0;
          lastScrollLeft = track.scrollLeft;
          settleRaf = window.requestAnimationFrame(watchForSettle);
        }
      },
      { passive: true }
    );

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        // Crossed back below the scroll breakpoint (e.g. grid layout resumed) —
        // drop the clones instead of resizing them against a non-scrolling track.
        if (!isScrollable(track)) {
          teardown(track);
          return;
        }
        var offsetWithinSet = track.scrollLeft - Math.floor(track.scrollLeft / setWidth) * setWidth;
        setWidth = firstReal.offsetLeft - before[0].offsetLeft;
        snapSafeJump(track, setWidth + offsetWithinSet);
      }, 150);
    });
  }

  function initAll(root) {
    var scope = root || document;
    var tracks = scope.querySelectorAll('[data-wit-infinite-track]');
    for (var i = 0; i < tracks.length; i++) init(tracks[i]);
  }

  // Tracks that start out non-scrollable (e.g. WIT Carousel's desktop grid)
  // never get their own resize listener from init(), so re-check globally
  // in case a resize makes them scrollable later (e.g. shrinking the window
  // below the mobile breakpoint without a reload).
  var globalResizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(globalResizeTimer);
    globalResizeTimer = window.setTimeout(function () { initAll(); }, 150);
  });

  window.WitInfiniteScroll = { init: init, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
